/* Shopify owns discount eligibility, allocations and rounding. No buyer cart is touched. */
(() => {
  const API_PATH = '/api/2026-07/graphql.json';
  const TIMEOUT_MS = 10000;
  const fields = `
    id
    lines(first: 2) {
      nodes {
        id quantity merchandise { ... on ProductVariant { id } }
        cost {
          amountPerQuantity { amount currencyCode }
          compareAtAmountPerQuantity { amount currencyCode }
          subtotalAmount { amount currencyCode }
        }
        discountAllocations(lineLevelOnly: false) {
          discountedAmount { amount currencyCode }
        }
      }
      pageInfo { hasNextPage }
    }`;
  const createQuery = `mutation ViannePriceCreate($input: CartInput!) {
    cartCreate(input: $input) { cart { ${fields} } userErrors { code } }
  }`;
  const updateQuery = `mutation ViannePriceUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ${fields} } userErrors { code } }
  }`;

  const money = (value, currency) => {
    if (value?.currencyCode !== currency || !/^\d+(\.\d+)?$/.test(value.amount)) {
      throw new Error('Shopify returned an invalid price or currency.');
    }
    const cents = Math.round(Number(value.amount) * 100);
    if (!Number.isSafeInteger(cents)) throw new Error('Shopify price is outside the supported range.');
    return cents;
  };

  const readQuote = (cart, request, currency) => {
    const line = cart?.lines?.nodes?.[0];
    if (!cart?.id || !line?.id || cart.lines.nodes.length !== 1 || cart.lines.pageInfo?.hasNextPage ||
        line.quantity !== request.quantity || line.merchandise?.id !== request.merchandiseId ||
        !Array.isArray(line.discountAllocations)) {
      throw new Error('Shopify could not quote the selected item and quantity.');
    }
    const unitPrice = money(line.cost?.amountPerQuantity, currency);
    const subtotal = money(line.cost?.subtotalAmount, currency);
    const savings = line.discountAllocations.reduce((sum, item) => sum + money(item.discountedAmount, currency), 0);
    const total = subtotal - savings;
    const compareAt = line.cost?.compareAtAmountPerQuantity
      ? money(line.cost.compareAtAmountPerQuantity, currency) : 0;
    if (total < 0 || subtotal > unitPrice * request.quantity) {
      throw new Error('Shopify returned inconsistent price totals.');
    }
    return { unitPrice, original: Math.max(unitPrice, compareAt), total, quantity: line.quantity };
  };

  const post = async (query, variables) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(API_PATH, {
        method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }), signal: controller.signal,
      });
      if (!response.ok) throw new Error('Shopify pricing is temporarily unavailable.');
      const payload = await response.json();
      const result = payload.data?.cartCreate || payload.data?.cartLinesUpdate;
      if (payload.errors?.length || !result || result.userErrors?.length) {
        throw new Error('Shopify could not calculate this price.');
      }
      return result.cart;
    } finally {
      clearTimeout(timer);
    }
  };

  const createClient = ({ country, currency }) => {
    // This secret identifies only an anonymous quote, never the customer's basket.
    let isolatedCart;
    let queue = Promise.resolve();
    const calculate = async (request) => {
      if (!/^[A-Z]{2}$/.test(country) || !/^[A-Z]{3}$/.test(currency) ||
          !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(request.merchandiseId) ||
          !Number.isSafeInteger(request.quantity) || request.quantity < 1) {
        throw new Error('Invalid Shopify pricing context.');
      }
      try {
        const cart = isolatedCart
          ? await post(updateQuery, { cartId: isolatedCart.id, lines: [{ id: isolatedCart.lineId, ...request }] })
          : await post(createQuery, { input: {
            buyerIdentity: { countryCode: country }, lines: [request],
            attributes: [{ key: '_vianne_purpose', value: 'price_preview' }],
          } });
        const quote = readQuote(cart, request, currency);
        isolatedCart = { id: cart.id, lineId: cart.lines.nodes[0].id };
        return quote;
      } catch (error) {
        isolatedCart = undefined;
        throw error;
      }
    };
    return (request) => {
      const result = queue.then(() => calculate(request));
      // A failed request must not poison the next quantity/focus retry.
      queue = result.then(() => undefined, () => undefined);
      return result;
    };
  };

  window.ViannePriceQuote = { createClient };
})();
